/** Pure helpers around the project-level constraint list. Kept
 *  paper-free (and side-effect-free) so the queries can be
 *  reused by the layers panel, properties panel, and any future
 *  enforcement code without coupling to a renderer. */

import type {
  Constraint,
  ConstraintKind,
  ConstraintRef,
  Project,
} from "./Project";

/** Read the project's constraints, defaulting to an empty array
 *  for projects authored before the field shipped. */
export function projectConstraints(project: Project): Constraint[] {
  return project.constraints ?? [];
}

/** True when `ref` targets the construction line `constructionId`,
 *  regardless of the ref kind (whole line, anchor on the line, or
 *  curve on the line). */
export function refTargetsLine(
  ref: ConstraintRef,
  constructionId: string,
): boolean {
  return ref.constructionId === constructionId;
}

/** All constraints that involve at least one ref pointing at the
 *  given construction line. Used by the layers panel to render
 *  per-line constraint chips and by the properties panel to list
 *  the constraints applying to the selected line. */
export function constraintsForLine(
  project: Project,
  constructionId: string,
): Constraint[] {
  return projectConstraints(project).filter((c) =>
    c.refs.some((ref) => refTargetsLine(ref, constructionId)),
  );
}

/** How many feature refs the picker needs to collect before it can
 *  commit a constraint of `kind`. Symmetry takes three (two mirrored
 *  features + the line of symmetry); every other initial-set kind
 *  takes two. The picker UI uses this to decide when to auto-commit
 *  vs. keep waiting for more clicks. */
export function requiredRefsForConstraint(kind: ConstraintKind): number {
  return kind === "symmetry" ? 3 : 2;
}

/** True when the picker / enforcement machinery has a working
 *  end-to-end implementation for `kind`. Drives the palette's
 *  disabled state — kinds that aren't implemented stay visible
 *  (so the user can see what's coming) but don't accept clicks.
 *  Today only coincident is fully wired (anchor↔anchor snap on
 *  create, drag, and whole-line move). */
export function isConstraintImplemented(kind: ConstraintKind): boolean {
  return kind === "coincident";
}

/** Which feature kinds (anchor / curve / line) are valid targets
 *  for a constraint of `kind`. The picker uses this to refuse
 *  clicks that don't make geometric sense — e.g. coincident is a
 *  *point* relationship, so a click landing on a curve or whole
 *  line shouldn't be captured even though those are otherwise
 *  hittable. Kinds that don't have a hard rule yet keep the
 *  permissive default ("any of the three") so today's UX doesn't
 *  regress for them; they tighten as their own picker rules land. */
export function allowedRefKindsForConstraint(
  kind: ConstraintKind,
): ReadonlyArray<"anchor" | "curve" | "line"> {
  // Coincident: two points pinned to the same spot. Only anchor
  // (vertex) refs are valid targets.
  if (kind === "coincident") return ["anchor"];
  return ["anchor", "curve", "line"];
}

/** Drop every constraint that touches one of the deleted
 *  construction lines. Called by the line-deletion paths so a
 *  constraint between L1 and L2 is removed *entirely* — not just
 *  from the deleted line — when L1 is deleted, leaving L2 free of
 *  the now-orphan constraint. Returns the input array unchanged
 *  (same reference) when nothing changes, so the caller can
 *  cheaply detect "no-op" without a deep diff. */
export function pruneConstraintsForDeletedLines(
  constraints: Constraint[] | undefined,
  deletedConstructionIds: ReadonlySet<string>,
): Constraint[] | undefined {
  if (!constraints || deletedConstructionIds.size === 0) return constraints;
  const filtered = constraints.filter(
    (c) => !c.refs.some((r) => deletedConstructionIds.has(r.constructionId)),
  );
  return filtered.length === constraints.length ? constraints : filtered;
}

/** Structural equality on two refs. Used to dedupe picks so the
 *  user can't accidentally satisfy a "needs two refs" constraint by
 *  double-clicking the same anchor. */
export function refsEqual(a: ConstraintRef, b: ConstraintRef): boolean {
  if (a.kind !== b.kind || a.constructionId !== b.constructionId) return false;
  if (a.kind === "anchor" && b.kind === "anchor") {
    return a.pathIndex === b.pathIndex && a.segmentIndex === b.segmentIndex;
  }
  if (a.kind === "curve" && b.kind === "curve") {
    return a.pathIndex === b.pathIndex && a.curveIndex === b.curveIndex;
  }
  if (a.kind === "bbox-midpoint" && b.kind === "bbox-midpoint") {
    return a.edge === b.edge;
  }
  if (a.kind === "center" && b.kind === "center") return true;
  return a.kind === "line" && b.kind === "line";
}
